import { Link, NavLink, useNavigate } from "react-router-dom";
import { Bell, LogOut, Menu, X, MessageSquare, HelpCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { getUser, logout, api, ROLE_LABEL, isAdminUser } from "../lib/api";
import { openExternal } from "../lib/native";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

// Backend gates `role: "admin"` to the whitelisted ADMIN_EMAILS set in
// `backend/deps.py`, so a role check is sufficient on the frontend.


export default function Layout({ children, hideChrome = false }) {
  const user = getUser();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const loadAll = () => {
      api.get(`/notifications/${user.id}`).then((r) => setNotifs(r.data)).catch(() => {});
      api.get(`/conversations/${user.id}`).then((r) => setConversations(r.data)).catch(() => {});
    };
    loadAll();
    const t = setInterval(loadAll, 30000);

    // WebSocket for real-time pushes
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backend.replace(/^http/, "ws") + `/api/ws/${user.id}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === "notification") {
            api.get(`/notifications/${user.id}`).then((r) => setNotifs(r.data)).catch(() => {});
            import("sonner").then(({ toast }) => toast(msg.title, { description: msg.body }));
          } else if (msg.type === "message") {
            api.get(`/conversations/${user.id}`).then((r) => setConversations(r.data)).catch(() => {});
            import("sonner").then(({ toast }) =>
              toast(`New message from ${msg.sender_name || "someone"}`, { description: msg.body?.slice(0, 80) })
            );
          }
        } catch { /* malformed ws message — ignore */ }
      };
    } catch { /* ws not supported / cross-origin — silent fallback to polling */ }

    return () => { clearInterval(t); ws && ws.close(); };
    // eslint-disable-next-line
  }, [user?.id]);

  const unread = notifs.filter((n) => !n.read).length;
  const unreadMessages = conversations.reduce((acc, c) => acc + (c.unread_count || 0), 0);

  const navItems = user
    ? [
        { to: "/dashboard", label: "Dashboard" },
        { to: "/marketplace", label: "Disposal" },
        { to: "/reuse", label: "Free stuff" },
        ...(isAdminUser(user) ? [{ to: "/admin", label: "Admin" }] : []),
      ]
    : [
        { to: "/marketplace", label: "Disposal" },
        { to: "/reuse", label: "Free stuff" },
      ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1A1A1A]">
      {!hideChrome && (
      <header className="sticky top-0 z-40 bg-[#FDFBF7]/85 backdrop-blur-md border-b border-[#E2DFD8]" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
            <img
              src="/icon-192.png?v=6"
              alt="Binshare"
              width="36"
              height="36"
              className="w-9 h-9 rounded-xl object-cover"
              data-testid="brand-logo"
            />
            <span className="font-medium text-xl tracking-tight" style={{ fontFamily: "Outfit" }}>
              Binshare
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    isActive
                      ? "bg-[#1B3624] text-white"
                      : "text-[#1A1A1A] hover:bg-[#EBE8E0]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                {/* Messages */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      data-testid="messages-bell"
                      variant="ghost"
                      size="icon"
                      className="relative rounded-full hover:bg-[#EBE8E0]"
                    >
                      <MessageSquare className="w-5 h-5" />
                      {unreadMessages > 0 && (
                        <span
                          data-testid="messages-unread-badge"
                          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D67B54] text-white text-[10px] font-medium flex items-center justify-center leading-none"
                        >
                          {unreadMessages > 9 ? "9+" : unreadMessages}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 rounded-2xl">
                    <DropdownMenuLabel className="flex items-center justify-between">
                      <span>Messages</span>
                      {unreadMessages > 0 && (
                        <Badge variant="secondary" className="bg-[#D67B54]/10 text-[#7A4A2A] border-[#D67B54]/30">{unreadMessages} unread</Badge>
                      )}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {conversations.length === 0 && (
                      <div className="px-3 py-6 text-center text-sm text-[#6B6965]">No conversations yet.</div>
                    )}
                    {conversations.slice(0, 8).map((c) => (
                      <DropdownMenuItem
                        key={c.conversation_id}
                        data-testid={`conversation-${c.peer_id}`}
                        onClick={() => {
                          if (c.waste_post_id) navigate(`/waste/${c.waste_post_id}`);
                          else if (c.bin_id) navigate(`/bin/${c.bin_id}`);
                        }}
                        className="cursor-pointer flex flex-col items-start gap-1 py-2.5"
                      >
                        <div className="flex items-center justify-between w-full gap-2">
                          <span className="font-medium text-sm text-[#1B3624] truncate">{c.peer_name || "Conversation"}</span>
                          {c.unread_count > 0 && (
                            <Badge className="bg-[#D67B54] text-white text-[10px] h-5 px-1.5">{c.unread_count}</Badge>
                          )}
                        </div>
                        <span className={`text-xs truncate w-full ${c.unread_count > 0 ? "text-[#1A1A1A]" : "text-[#6B6965]"}`}>
                          {c.last_flagged && "⚠️ "}{c.last_body}
                        </span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      data-testid="notifications-bell"
                      variant="ghost"
                      size="icon"
                      className="relative rounded-full hover:bg-[#EBE8E0]"
                    >
                      <Bell className="w-5 h-5" />
                      {unread > 0 && (
                        <span
                          data-testid="notifications-unread"
                          className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#D67B54]"
                        />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 rounded-2xl">
                    <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {notifs.length === 0 && (
                      <div className="px-3 py-6 text-center text-sm text-[#6B6965]">
                        Nothing yet. We'll ping you here.
                      </div>
                    )}
                    {notifs.slice(0, 6).map((n) => (
                      <DropdownMenuItem
                        key={n.id}
                        data-testid={`notif-${n.id}`}
                        onClick={() => {
                          api.post(`/notifications/${n.id}/read`).catch(() => {});
                          if (n.link) navigate(n.link);
                        }}
                        className="flex flex-col items-start gap-1 py-3 cursor-pointer"
                      >
                        <span className="text-sm font-medium">{n.title}</span>
                        <span className="text-xs text-[#6B6965]">{n.body}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="user-menu" className="flex items-center gap-2 rounded-full hover:bg-[#EBE8E0] p-1 pr-3">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-[#4A6B56] text-white text-xs">
                          {user.name?.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium hidden sm:inline">{user.name.split(" ")[0]}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 rounded-2xl">
                    <DropdownMenuLabel className="flex flex-col gap-1">
                      <span>{user.name}</span>
                      <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] w-fit text-[10px] uppercase tracking-wide">
                        {ROLE_LABEL[user.role]}
                      </Badge>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/faq")} data-testid="menu-faq">
                      Help / FAQ
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        logout();
                        navigate("/");
                      }}
                      data-testid="menu-logout"
                    >
                      <LogOut className="w-4 h-4 mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <button
                  type="button"
                  data-testid="help-btn"
                  onClick={() => navigate("/faq")}
                  aria-label="Help / FAQ"
                  className="hidden sm:inline-flex w-9 h-9 rounded-full items-center justify-center text-[#1B3624] hover:bg-[#EBE8E0] transition-colors"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
                <Button
                  data-testid="login-btn"
                  onClick={() => navigate("/login")}
                  className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white px-5"
                >
                  Sign in
                </Button>
              </>
            )}
            <button
              data-testid="mobile-menu-btn"
              className="md:hidden p-2 rounded-full hover:bg-[#EBE8E0]"
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-[#E2DFD8] px-5 py-3 space-y-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-xl text-sm ${
                    isActive ? "bg-[#1B3624] text-white" : "hover:bg-[#EBE8E0]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        )}
      </header>
      )}
      <main>{children}</main>
      {!hideChrome && (
      <footer className="mt-20 border-t border-[#E2DFD8] py-10 text-center text-sm text-[#6B6965]" style={{ paddingBottom: "max(2.5rem, env(safe-area-inset-bottom))" }}>
        <div className="max-w-7xl mx-auto px-5 space-y-3">
          <div>Binshare — licensed waste removal & reuse, across the UK.</div>
          <div data-testid="footer-store-links" className="flex items-center justify-center gap-3">
            <a
              href="https://apps.apple.com/gb/app/bin-share-app/id6766317157"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-store-appstore"
              aria-label="Download Binshare on the App Store"
              className="inline-flex"
            >
              <img
                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-gb"
                alt="Download on the App Store"
                className="h-9 w-auto"
                loading="lazy"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=uk.co.binshare.marketplace"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-store-playstore"
              aria-label="Get Binshare on Google Play"
              className="inline-flex"
            >
              <img
                src="https://play.google.com/intl/en_gb/badges/static/images/badges/en_badge_web_generic.png"
                alt="Get it on Google Play"
                className="h-9 w-auto"
                loading="lazy"
              />
            </a>
          </div>
          <div className="flex items-center justify-center gap-4 text-xs flex-wrap">
            <Link to="/terms" data-testid="footer-terms" className="hover:text-[#1B3624]">Terms of Service</Link>
            <span>·</span>
            <Link to="/privacy" data-testid="footer-policies" className="hover:text-[#1B3624]">Privacy</Link>
            <span>·</span>
            <Link to="/faq" data-testid="footer-faq" className="hover:text-[#1B3624]">Help / FAQ</Link>
            <span>·</span>
            <button
              type="button"
              data-testid="footer-cookie-prefs"
              onClick={() => {
                try {
                  window.localStorage.removeItem("binshare_cookie_consent");
                  window.dispatchEvent(new CustomEvent("binshare-consent-change", { detail: null }));
                } catch { /* private mode — no-op */ }
              }}
              className="hover:text-[#1B3624] underline-offset-2 hover:underline"
            >
              Cookie preferences
            </button>
            <span>·</span>
            <a href="mailto:support@binshare.co.uk" data-testid="footer-support-email" className="hover:text-[#1B3624]">support@binshare.co.uk</a>
            <span>·</span>
            <a href="https://binshare.co.uk" onClick={(e) => { e.preventDefault(); openExternal("https://binshare.co.uk"); }} className="hover:text-[#1B3624]">binshare.co.uk</a>
          </div>
        </div>
      </footer>
      )}
    </div>
  );
}
